import styled from '@emotion/styled'
import clsx from 'clsx'
import { observer } from 'mobx-react'

import { typo } from '~/@common/styles'
import { mediaQuery } from '~/@common/styles/mediaQuery'
import _Switch from '~/@common/ui/_Switch/_Switch'
import { Dropdown, DropdownItem } from '~/@common/ui/Dropdown'

import { GradingResultNotification } from '../GradingResultNotification'
import type { ProblemScoringViewOption } from './@trait/ProblemScoringViewOption.trait'

const FILTER_LABEL = {
  전체보기: '전체 보기',
  오답_모르는문제만보기: '오답/모르는 문제만 보기',
  안푼문제만보기: '안 푼 문제만 보기',
  문제같이보기: '문제 같이 보기',
} as const

const FILTER_OPTIONS = [
  { value: '', label: FILTER_LABEL.전체보기 },
  { value: '안푼문제만보기', label: FILTER_LABEL.안푼문제만보기 },
  { value: '오답_모르는문제만보기', label: FILTER_LABEL.오답_모르는문제만보기 },
] as const
type FilterValue = (typeof FILTER_OPTIONS)[number]['value']

const toFilterValue = (
  viewOption: ProblemScoringViewOption<'학습모듈' | 'NOT_학습모듈'>,
): FilterValue => {
  if (viewOption.오답_모르는문제만보기) return '오답_모르는문제만보기'
  if (viewOption.안푼문제만보기) return '안푼문제만보기'
  return ''
}

const ProblemScoringViewGuideOption = ({
  viewOption,
  guideDisabled = false,
}: {
  viewOption?: ProblemScoringViewOption<'학습모듈' | 'NOT_학습모듈'>
  guideDisabled?: boolean
}) => {
  if (!viewOption) {
    return <S.ProblemScoringViewGuideOption />
  }
  const supportDropDown =
    viewOption.오답_모르는문제만보기_노출여부 &&
    viewOption.안푼문제만보기_노출여부 &&
    viewOption.문제같이보기_노출여부

  return (
    <S.ProblemScoringViewGuideOption className={clsx(supportDropDown && 'has-dropdown')}>
      {guideDisabled ? null : viewOption.가이드_노출 === undefined ? (
        <></>
      ) : viewOption.가이드_노출 === '가이드_일반채점' ? (
        <div className="left">
          <GradingResultNotification />
        </div>
      ) : (
        <div className="left">
          채점 후 답과 해설이 비공개되어 있습니다. 선생님에게 문의해주세요.
        </div>
      )}
      {supportDropDown ? (
        <div className="right">
          <Dropdown
            width={168}
            value={toFilterValue(viewOption)}
            onChange={(value: FilterValue) => {
              viewOption.onToggle({
                오답_모르는문제만보기: value === '오답_모르는문제만보기',
                안푼문제만보기: value === '안푼문제만보기',
              })
            }}
          >
            {FILTER_OPTIONS.map((option) => (
              <DropdownItem key={option.value} value={option.value}>
                {option.label}
              </DropdownItem>
            ))}
          </Dropdown>
          <_Switch
            className="문제같이보기_토글"
            checked={viewOption.문제같이보기}
            onChange={(e) => {
              viewOption.onToggle({
                문제같이보기: e.target.checked,
              })
            }}
          >
            {FILTER_LABEL.문제같이보기}
          </_Switch>
        </div>
      ) : (
        (viewOption.오답_모르는문제만보기_노출여부 ||
          viewOption.안푼문제만보기_노출여부 ||
          viewOption.문제같이보기_노출여부) && (
          <div className="right">
            {viewOption.오답_모르는문제만보기_노출여부 && (
              <_Switch
                checked={viewOption.오답_모르는문제만보기}
                onChange={(e) => {
                  viewOption.onToggle({
                    오답_모르는문제만보기: e.target.checked,
                  })
                }}
              >
                {FILTER_LABEL.오답_모르는문제만보기}
              </_Switch>
            )}
            {viewOption.안푼문제만보기_노출여부 && (
              <_Switch
                checked={viewOption.안푼문제만보기}
                onChange={(e) => {
                  viewOption.onToggle({ 안푼문제만보기: e.target.checked })
                }}
              >
                {FILTER_LABEL.안푼문제만보기}
              </_Switch>
            )}
            {viewOption.문제같이보기_노출여부 && (
              <_Switch
                className="문제같이보기_토글"
                checked={viewOption.문제같이보기}
                onChange={(e) => {
                  viewOption.onToggle({
                    문제같이보기: e.target.checked,
                  })
                }}
              >
                {FILTER_LABEL.문제같이보기}
              </_Switch>
            )}
          </div>
        )
      )}
    </S.ProblemScoringViewGuideOption>
  )
}

export default observer(ProblemScoringViewGuideOption)

const S = {
  ProblemScoringViewGuideOption: styled.div`
    display: flex;
    align-items: center;
    ${typo.caption01};
    height: 22px;

    &.has-dropdown {
      height: auto;
    }

    > .left {
      display: flex;
      align-items: center;
      margin-right: auto;
    }
    > .right {
      display: flex;
      align-items: center;
      gap: 28px;
      margin-left: auto;
    }

    .문제같이보기_토글 {
      margin-left: auto;
    }

    ${mediaQuery.underTablet} {
      width: 100%;
      height: auto;
      flex-direction: column;
      > .left,
      > .right {
        width: 100%;
        padding: 10px 20px;
        border-bottom: 1px solid var(--Colors-Gray-300);
      }
      > .right {
        display: flex;
        justify-content: space-between;
      }
    }
  `,
}
